import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Chip from "@mui/joy/Chip";
import ChipDelete from "@mui/joy/ChipDelete";
import CircularProgress from "@mui/joy/CircularProgress";
import Divider from "@mui/joy/Divider";
import IconButton from "@mui/joy/IconButton";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import React from "react";
import { Question } from "../../../models/questionnaire";
import { Coding } from "../../../models/structuration";
import { DrawerWrapper } from "../../../shared/components/layout/DrawerWrapper";
import { SearchBar } from "../../../shared/components/search/SearchBar";

interface QuestionnaireDrawerProps {
  readonly onClose: () => void;
  readonly questions: Question[];
  readonly onSave: (questions: Question[]) => Promise<void>;
}

export default function QuestionnaireDrawer({
  onClose,
  questions,
  onSave,
}: QuestionnaireDrawerProps) {
  const [isSaving, setIsSaving] = React.useState(false);
  const [hasChanges, setHasChanges] = React.useState(false);
  const [selectedQuestions, setSelectedQuestions] = React.useState<Question[]>(questions);

  const handleSave = async () => {
    setIsSaving(true);
    await onSave(selectedQuestions);
    setIsSaving(false);
    onClose();
  };

  const onQuestionRemoved = (index: number) => {
    setSelectedQuestions((prev) => prev.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const handleAddQuestion = (coding: Coding | null) => {
    if (!coding) return;
    const question: Question = {
      text: coding.display || "Unnamed Question",
      main_concept: [coding],
    };
    setSelectedQuestions((prev) => [...prev, question]);
    setHasChanges(true);
  };

  return (
    <DrawerWrapper>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          backgroundColor: "white",
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, display: "flex", alignItems: "center" }}>
          <Typography level="title-md" sx={{ flex: 1 }}>
            Manage questions
          </Typography>
          <IconButton component="span" variant="plain" color="neutral" size="sm" onClick={onClose}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>
        <Divider />
        {/* Main Content (scrollable area) */}
        <Box sx={{ flexGrow: 1, overflowY: "auto", p: 2 }}>
          <Stack direction="column" gap={2}>
            <Typography level="body-md">
              Select concepts of interest to include in the questionnaire.
            </Typography>
            <SearchBar message="Search for a concept" onChange={(c) => handleAddQuestion(c)} />
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              {selectedQuestions.map((question, index) => {
                const key = question.text || `question-${index}`;
                return (
                  <Chip
                    key={key}
                    size="lg"
                    variant="solid"
                    color="neutral"
                    endDecorator={<ChipDelete onDelete={() => onQuestionRemoved(index)} />}
                  >
                    {question.text || "Unnamed Question"}
                  </Chip>
                );
              })}
            </Box>
          </Stack>
        </Box>
        {/* Footer: Save Button */}
        <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
          <Button
            variant="solid"
            fullWidth
            onClick={handleSave}
            startDecorator={isSaving ? <CircularProgress size="sm" /> : null}
            disabled={isSaving || !hasChanges}
            loading={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>
    </DrawerWrapper>
  );
}
